import React, { useEffect, useMemo, useState } from "react";
import {
  makeStyles,
  Grid,
  TextField,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  IconButton,
  Paper,
} from "@material-ui/core";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import {
  HelpCircle,
  Pencil,
  Trash2,
  Eraser,
  Save,
  Trash,
  Youtube,
} from "lucide-react";

import AppTooltip from "../AppTooltip";
import ButtonWithSpinner from "../ButtonWithSpinner";
import ConfirmationModal from "../ConfirmationModal";
import TableRowSkeleton from "../TableRowSkeleton";
import useHelps from "../../hooks/useHelps";
import { i18n } from "../../translate/i18n";

const YT_ID_REGEX = /^[A-Za-z0-9_-]{11}$/;
const YT_URL_REGEX =
  /(?:youtube\.com\/watch\?[^#]*v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/|youtube\.com\/v\/)([A-Za-z0-9_-]{11})/;

const extractYoutubeId = (input) => {
  if (!input) return "";
  const trimmed = String(input).trim();
  if (YT_ID_REGEX.test(trimmed)) return trimmed;
  const m = trimmed.match(YT_URL_REGEX);
  return m ? m[1] : "";
};

const buildYoutubeUrl = (id) => (id ? `https://youtu.be/${id}` : "");

const useStyles = makeStyles((theme) => {
  const ds = theme.chatiaDS || {};
  const dsColors = ds.colors || {};
  const isDark = theme.mode === "dark" || theme.palette?.type === "dark";
  return {
    container: {
      flex: 1,
      minHeight: 0,
      height: "100%",
      padding: 12,
      overflowY: "auto",
      overflowX: "hidden",
      display: "flex",
      flexDirection: "column",
      gap: 14,
      ...(theme.scrollbarStyles || {}),
    },
    pageHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
      flexWrap: "wrap",
      padding: "4px 4px 4px",
    },
    headerLeft: {
      display: "flex",
      alignItems: "center",
      gap: 12,
    },
    pageIconWrap: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: 36,
      height: 36,
      borderRadius: 10,
      background: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
      color: theme.palette.primary.main,
    },
    pageTitle: {
      fontSize: 18,
      fontWeight: 600,
      color: dsColors.textPrimary || (isDark ? "#e9edef" : "#1a1a1a"),
      lineHeight: 1.2,
      display: "inline-flex",
      alignItems: "center",
      gap: 10,
    },
    pageSubtitle: {
      fontSize: 12,
      color:
        dsColors.textSecondary ||
        (isDark ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.55)"),
      marginTop: 2,
    },
    countChip: {
      display: "inline-flex",
      alignItems: "center",
      padding: "2px 9px",
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 700,
      letterSpacing: 0.2,
      background: isDark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.06)",
      color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
    },
    card: {
      borderRadius: 12,
      background: dsColors.surfaceDefault || (isDark ? "#11141b" : "#f7f8fa"),
      border: `1px solid ${
        dsColors.border || (isDark ? "#202632" : "#e8e8e8")
      }`,
      boxShadow: isDark
        ? "0 2px 12px rgba(0,0,0,0.35)"
        : "0 2px 12px rgba(15,23,42,0.06)",
      padding: 16,
    },
    cardHeader: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      marginBottom: 14,
      fontSize: 13,
      fontWeight: 600,
      color: dsColors.textPrimary || (isDark ? "#e9edef" : "#1a1a1a"),
    },
    cardHeaderIcon: {
      color: theme.palette.primary.main,
    },
    fieldsRow: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr 2fr",
      gap: 14,
      [theme.breakpoints.down("sm")]: {
        gridTemplateColumns: "1fr",
      },
    },
    fieldGroup: {
      display: "flex",
      flexDirection: "column",
      gap: 6,
    },
    fieldLabel: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      fontSize: 12.5,
      fontWeight: 600,
      color: dsColors.textPrimary || (isDark ? "#e9edef" : "#111b21"),
      lineHeight: 1.2,
    },
    helpIcon: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      verticalAlign: "middle",
      color: isDark ? "#8696a0" : "#667781",
      cursor: "help",
      opacity: 0.6,
      transition: "opacity 0.15s ease, color 0.15s ease",
      "&:hover": {
        opacity: 1,
        color: theme.palette.primary.main,
      },
    },
    helpTooltipTitle: {
      fontWeight: 600,
      marginBottom: 6,
      fontSize: 12,
    },
    helpTooltipSteps: {
      margin: 0,
      paddingLeft: 16,
      "& li": { marginBottom: 3 },
      "& li:last-child": { marginBottom: 0 },
    },
    roundedField: {
      "& .MuiOutlinedInput-root": {
        borderRadius: 12,
      },
      "& .MuiOutlinedInput-input": {
        padding: "12px 14px",
      },
    },
    fieldError: {
      fontSize: 11.5,
      color: "#e53935",
      marginTop: 2,
    },
    formActions: {
      display: "flex",
      justifyContent: "flex-end",
      gap: 8,
      marginTop: 16,
      flexWrap: "wrap",
    },
    btnBase: {
      borderRadius: 12,
      textTransform: "none",
      fontWeight: 500,
      fontSize: 13,
      padding: "8px 16px",
      gap: 6,
      minHeight: 38,
      boxShadow: "none",
      "&:hover": { boxShadow: "none" },
    },
    btnClear: {
      background: isDark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.06)",
      color: dsColors.textPrimary || (isDark ? "#e9edef" : "#1a1a1a"),
      "&:hover": {
        background: isDark ? "rgba(255,255,255,0.1)" : "rgba(15,23,42,0.1)",
      },
    },
    btnDelete: {
      background: "linear-gradient(135deg, #e53935 0%, #c62828 100%)",
      color: "#fff",
      "&:hover": {
        background: "linear-gradient(135deg, #c62828 0%, #a91a1a 100%)",
      },
    },
    btnSave: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText || "#fff",
      "&:hover": { backgroundColor: theme.palette.primary.dark },
    },
    tableCard: {
      flex: 1,
      borderRadius: 12,
      background: dsColors.surfaceDefault || (isDark ? "#11141b" : "#f7f8fa"),
      border: `1px solid ${
        dsColors.border || (isDark ? "#202632" : "#e8e8e8")
      }`,
      boxShadow: isDark
        ? "0 2px 12px rgba(0,0,0,0.35)"
        : "0 2px 12px rgba(15,23,42,0.06)",
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
      minHeight: 220,
    },
    tableScroll: {
      flex: 1,
      overflowY: "auto",
      overflowX: "auto",
      ...(theme.scrollbarStyles || {}),
    },
    table: {
      borderCollapse: "separate",
      borderSpacing: 0,
      "& .MuiTableCell-root": {
        borderBottom: `1px solid ${
          dsColors.borderLight || (isDark ? "#1a1f29" : "#f0f0f0")
        }`,
        color: dsColors.textPrimary || (isDark ? "#e9edef" : "#111b21"),
        fontSize: 13.5,
        padding: "14px 16px",
      },
    },
    tableHead: {
      "& .MuiTableCell-head": {
        background: isDark
          ? "rgba(255,255,255,0.02)"
          : "rgba(15,23,42,0.03)",
        color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
        fontWeight: 700,
        fontSize: 11.5,
        textTransform: "uppercase",
        letterSpacing: 0.6,
        borderBottom: `1px solid ${
          dsColors.border || (isDark ? "#202632" : "#e8e8e8")
        }`,
        padding: "12px 16px",
        whiteSpace: "nowrap",
      },
    },
    tableRow: {
      transition: "background-color 0.18s ease",
      "&:hover": {
        backgroundColor:
          dsColors.surfaceHover || (isDark ? "#171b24" : "#f3f4f6"),
      },
      "&:last-child .MuiTableCell-root": {
        borderBottom: "none",
      },
    },
    activeRow: {
      backgroundColor: isDark
        ? "rgba(99,102,241,0.08)"
        : "rgba(99,102,241,0.06)",
      "&:hover": {
        backgroundColor: isDark
          ? "rgba(99,102,241,0.12)"
          : "rgba(99,102,241,0.1)",
      },
    },
    titleCell: {
      fontWeight: 600,
    },
    descCell: {
      maxWidth: 480,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    videoCodeChip: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "3px 8px",
      borderRadius: 6,
      fontFamily:
        "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
      fontSize: 12,
      background: isDark ? "rgba(255,255,255,0.05)" : "rgba(15,23,42,0.05)",
      color: dsColors.textPrimary || (isDark ? "#e9edef" : "#111b21"),
    },
    iconActionBtn: {
      borderRadius: 10,
      padding: 6,
      marginRight: 4,
      transition: "all 0.18s ease",
      color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
      "&:hover": {
        background: isDark
          ? "rgba(255,255,255,0.06)"
          : "rgba(15,23,42,0.06)",
        color: dsColors.textPrimary || (isDark ? "#e9edef" : "#111b21"),
      },
    },
    deleteActionBtn: {
      borderRadius: 10,
      padding: 6,
      transition: "all 0.18s ease",
      color: "#e53935",
      "&:hover": {
        background: "rgba(229,57,53,0.10)",
        color: "#c62828",
      },
    },
    emptyState: {
      padding: "60px 16px",
      textAlign: "center",
      color: isDark ? "#8696a0" : "#667781",
      fontSize: 13.5,
    },
    actionsCell: {
      width: 1,
      whiteSpace: "nowrap",
    },
  };
});

const buildHelpContent = (classes, key) => {
  const data = i18n.t(`helps.tooltips.${key}`, {
    returnObjects: true,
    defaultValue: null,
  });
  if (!data || typeof data === "string") return null;
  const description = data.description || "";
  const steps = Array.isArray(data.steps) ? data.steps : [];
  if (!description && steps.length === 0) return null;
  return (
    <div>
      {description && (
        <div className={classes.helpTooltipTitle}>{description}</div>
      )}
      {steps.length > 0 && (
        <ol className={classes.helpTooltipSteps}>
          {steps.map((s, i) => (
            <li key={i}>{s}</li>
          ))}
        </ol>
      )}
    </div>
  );
};

const HelpIcon = ({ classes, helpKey }) => {
  const content = buildHelpContent(classes, helpKey);
  if (!content) return null;
  return (
    <AppTooltip title={content} enterTouchDelay={0} leaveTouchDelay={6000}>
      <span className={classes.helpIcon}>
        <HelpCircle size={14} strokeWidth={2} />
      </span>
    </AppTooltip>
  );
};

export function HelpManagerForm(props) {
  const { onSubmit, onDelete, onCancel, initialValue, loading } = props;
  const classes = useStyles();
  const [record, setRecord] = useState(initialValue);

  useEffect(() => {
    setRecord(initialValue);
  }, [initialValue]);

  const handleFormSubmit = (values, { resetForm, setFieldError }) => {
    const id = extractYoutubeId(values.video);
    if (values.video && !id) {
      setFieldError("video", i18n.t("helps.form.invalidLink"));
      return;
    }
    setTimeout(() => {
      onSubmit({ ...values, video: id });
      resetForm();
    }, 300);
  };

  return (
    <div className={classes.card}>
      <div className={classes.cardHeader}>
        <span className={classes.cardHeaderIcon}>
          <Youtube size={16} strokeWidth={2} />
        </span>
        {record?.id !== undefined
          ? i18n.t("helps.form.editTitle")
          : i18n.t("helps.form.newTitle")}
      </div>
      <Formik
        enableReinitialize
        initialValues={record}
        onSubmit={handleFormSubmit}
      >
        {({ errors, touched }) => (
          <Form>
            <div className={classes.fieldsRow}>
              <div className={classes.fieldGroup}>
                <label className={classes.fieldLabel}>
                  {i18n.t("helps.form.titleLabel")}
                  <HelpIcon classes={classes} helpKey="title" />
                </label>
                <Field
                  as={TextField}
                  name="title"
                  variant="outlined"
                  size="small"
                  fullWidth
                  className={classes.roundedField}
                  placeholder={i18n.t("helps.form.titlePlaceholder")}
                />
              </div>

              <div className={classes.fieldGroup}>
                <label className={classes.fieldLabel}>
                  {i18n.t("helps.form.linkLabel")}
                  <HelpIcon classes={classes} helpKey="link" />
                </label>
                <Field
                  as={TextField}
                  name="video"
                  variant="outlined"
                  size="small"
                  fullWidth
                  className={classes.roundedField}
                  placeholder={i18n.t("helps.form.linkPlaceholder")}
                  error={Boolean(errors.video && touched.video)}
                />
                {errors.video && touched.video && (
                  <div className={classes.fieldError}>{errors.video}</div>
                )}
              </div>

              <div className={classes.fieldGroup}>
                <label className={classes.fieldLabel}>
                  {i18n.t("helps.form.descriptionLabel")}
                  <HelpIcon classes={classes} helpKey="description" />
                </label>
                <Field
                  as={TextField}
                  name="description"
                  variant="outlined"
                  size="small"
                  fullWidth
                  className={classes.roundedField}
                  placeholder={i18n.t("helps.form.descriptionPlaceholder")}
                />
              </div>
            </div>

            <div className={classes.formActions}>
              <ButtonWithSpinner
                type="button"
                className={`${classes.btnBase} ${classes.btnClear}`}
                loading={loading}
                onClick={() => onCancel()}
                variant="contained"
                disableElevation
                startIcon={<Eraser size={15} strokeWidth={2} />}
              >
                {i18n.t("helps.settings.clear")}
              </ButtonWithSpinner>
              {record?.id !== undefined && (
                <ButtonWithSpinner
                  type="button"
                  className={`${classes.btnBase} ${classes.btnDelete}`}
                  loading={loading}
                  onClick={() => onDelete(record)}
                  variant="contained"
                  disableElevation
                  startIcon={<Trash size={15} strokeWidth={2} />}
                >
                  {i18n.t("helps.settings.delete")}
                </ButtonWithSpinner>
              )}
              <ButtonWithSpinner
                type="submit"
                className={`${classes.btnBase} ${classes.btnSave}`}
                loading={loading}
                variant="contained"
                color="primary"
                disableElevation
                startIcon={<Save size={15} strokeWidth={2} />}
              >
                {i18n.t("helps.settings.save")}
              </ButtonWithSpinner>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
}

export function HelpsManagerGrid(props) {
  const { records, onSelect, onDelete, activeId, loading } = props;
  const classes = useStyles();

  return (
    <Paper className={classes.tableCard} variant="outlined" elevation={0}>
      <div className={classes.tableScroll}>
        <Table size="small" className={classes.table}>
          <TableHead className={classes.tableHead}>
            <TableRow>
              <TableCell align="left">{i18n.t("helps.table.title")}</TableCell>
              <TableCell align="left">
                {i18n.t("helps.table.description")}
              </TableCell>
              <TableCell align="left">{i18n.t("helps.table.video")}</TableCell>
              <TableCell align="right" className={classes.actionsCell}>
                {i18n.t("helps.table.actions")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && records.length === 0 ? (
              <TableRowSkeleton columns={4} />
            ) : records.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className={classes.emptyState}>
                  {i18n.t("helps.empty.list")}
                </TableCell>
              </TableRow>
            ) : (
              records.map((row) => (
                <TableRow
                  key={row.id}
                  className={`${classes.tableRow} ${
                    activeId === row.id ? classes.activeRow : ""
                  }`}
                >
                  <TableCell align="left" className={classes.titleCell}>
                    {row.title || "-"}
                  </TableCell>
                  <TableCell align="left" className={classes.descCell}>
                    {row.description || "-"}
                  </TableCell>
                  <TableCell align="left">
                    {row.video ? (
                      <span className={classes.videoCodeChip}>
                        <Youtube size={13} strokeWidth={2} />
                        {row.video}
                      </span>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                  <TableCell align="right" className={classes.actionsCell}>
                    <IconButton
                      size="small"
                      className={classes.iconActionBtn}
                      onClick={() => onSelect(row)}
                      aria-label="edit"
                    >
                      <Pencil size={16} strokeWidth={1.8} />
                    </IconButton>
                    {onDelete && (
                      <IconButton
                        size="small"
                        className={classes.deleteActionBtn}
                        onClick={() => onDelete(row)}
                        aria-label="delete"
                      >
                        <Trash2 size={16} strokeWidth={1.8} />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </Paper>
  );
}

export default function HelpsManager() {
  const classes = useStyles();
  const { list, save, update, remove } = useHelps();

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [record, setRecord] = useState({
    title: "",
    description: "",
    video: "",
  });

  const loadHelps = async () => {
    setLoading(true);
    try {
      const helpList = await list();
      setRecords(helpList || []);
    } catch (e) {
      toast.error(i18n.t("settings.toasts.recordsLoadError"));
    }
    setLoading(false);
  };

  useEffect(() => {
    loadHelps();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (data) => {
    setLoading(true);
    try {
      if (data.id !== undefined) {
        await update(data);
      } else {
        await save(data);
      }
      await loadHelps();
      handleCancel();
      toast.success(i18n.t("settings.toasts.operationSuccess"));
    } catch (e) {
      toast.error(i18n.t("settings.toasts.helpOperationError"));
    }
    setLoading(false);
  };

  const handleConfirmDelete = async () => {
    const target = pendingDelete || record;
    if (!target?.id) {
      setShowConfirmDialog(false);
      return;
    }
    setLoading(true);
    try {
      await remove(target.id);
      await loadHelps();
      handleCancel();
      setShowConfirmDialog(false);
      setPendingDelete(null);
      toast.success(i18n.t("settings.toasts.operationSuccess"));
    } catch (e) {
      toast.error(i18n.t("settings.toasts.operationDeleteError"));
    }
    setLoading(false);
  };

  const handleAskDeleteFromForm = () => {
    setPendingDelete(record);
    setShowConfirmDialog(true);
  };

  const handleAskDeleteFromRow = (row) => {
    setPendingDelete(row);
    setShowConfirmDialog(true);
  };

  const handleCancel = () => {
    setRecord({ title: "", description: "", video: "" });
  };

  const handleSelect = (data) => {
    setRecord({
      id: data.id,
      title: data.title || "",
      description: data.description || "",
      video: buildYoutubeUrl(data.video),
    });
  };

  const subtitle = useMemo(() => i18n.t("helps.subtitle"), []);

  return (
    <div className={classes.container}>
      <div className={classes.pageHeader}>
        <div className={classes.headerLeft}>
          <span className={classes.pageIconWrap}>
            <HelpCircle size={20} strokeWidth={1.8} />
          </span>
          <div>
            <div className={classes.pageTitle}>
              {i18n.t("helps.title")}
              <span className={classes.countChip}>{records.length}</span>
            </div>
            <div className={classes.pageSubtitle}>{subtitle}</div>
          </div>
        </div>
      </div>

      <Grid container spacing={0}>
        <Grid xs={12} item>
          <HelpManagerForm
            initialValue={record}
            onDelete={handleAskDeleteFromForm}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            loading={loading}
          />
        </Grid>
      </Grid>

      <HelpsManagerGrid
        records={records}
        onSelect={handleSelect}
        onDelete={handleAskDeleteFromRow}
        activeId={record?.id}
        loading={loading}
      />

      <ConfirmationModal
        title={
          pendingDelete?.title
            ? `${i18n.t("settings.modals.deleteTitle")} ${pendingDelete.title}?`
            : i18n.t("settings.modals.deleteTitle")
        }
        open={showConfirmDialog}
        onClose={() => {
          setShowConfirmDialog(false);
          setPendingDelete(null);
        }}
        onConfirm={handleConfirmDelete}
      >
        {i18n.t("settings.modals.deleteConfirmation")}
      </ConfirmationModal>
    </div>
  );
}
